/**
 * MessagingParser.js
 * Utility functions for parsing and processing messaging app links
 * (WhatsApp, Telegram, Viber, etc.)
 */

// Constants
export const MESSAGING_PROTOCOLS = [
    'whatsapp:', 
    'telegram:', 
    'viber:', 
    'fb-messenger:', 
    'sms:', 
    'vk:',
    'tg:'
  ];
  
  export const MESSENGER_PATTERNS = [
    'api.whatsapp.com/send', 
    'wa.me/', 
    'm.me/', 
    'messenger.com', 
    't.me/', 
    'telegram.me/', 
    'viber://', 
    'vk.com/im', 
    'vk.me/', 
    'chat.whatsapp.com', 
    'web.telegram.org', 
    'tlgg.ru/', 
    'line.me/', 
    'discordapp.com/channels', 
    'discord.com/channels', 
    'signal.me/', 
    'teams.microsoft.com', 
    'slack.com/app_redirect'
  ];
  
  const UTM_PARAMETERS = [
    'utm_source=',
    'utm_medium=',
    'utm_campaign=',
    'utm_content=',
    'utm_term=',
    'utm_id='
  ];
  
  /**
   * Check if a URL has UTM parameters
   *
   * Case-insensitive (F1, T3 — repo-review finding alongside the shortener
   * detector): `?UTM_SOURCE=x` is functionally identical to `?utm_source=x`
   * to every real ad platform and to GA4 itself, but the original
   * case-sensitive `.includes()` here silently treated it as "no UTM" —
   * re-tagging (and, worse, double-tagging) a link that already carried an
   * upper/mixed-case utm_* param.
   *
   * @param {string} url The URL to check
   * @return {boolean} True if the URL has UTM parameters
   */
  export function hasUtmParameters(url) {
    if (!url || typeof url !== 'string') return false;

    const lowerUrl = url.toLowerCase();
    return UTM_PARAMETERS.some(param => lowerUrl.includes(param));
  }
  
  /**
   * Check if a link is from a messaging application
   * 
   * @param {HTMLAnchorElement|string} link The link element or URL string to check
   * @return {boolean} True if the link is a messaging app link
   */
  export function isMessagingLink(link) {
    const href = typeof link === 'string' ? link : (link?.href || '');
    if (!href) return false;
    
    const lowercaseHref = href.toLowerCase();
    
    // Check for direct protocol usage
    if (MESSAGING_PROTOCOLS.some(protocol => lowercaseHref.startsWith(protocol))) {
      return true;
    }
    
    // Check for common messenger URL patterns
    if (MESSENGER_PATTERNS.some(pattern => lowercaseHref.includes(pattern))) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Extract URL from text content
   * 
   * @param {string} text The text to search for URLs
   * @return {string|null} The first URL found or null
   */
  function extractUrlFromText(text) {
    if (!text) return null;
    
    const urlMatch = text.match(/(https?:\/\/[^\s]+)/i);
    return urlMatch ? urlMatch[0] : null;
  }
  
  /**
   * Process WhatsApp links
   * 
   * @param {string} url The WhatsApp URL
   * @return {Object|null} Parsed data or null
   */
  function processWhatsAppLink(url) {
    const textMatch = url.match(/[?&]text=([^&]+)/i);
    if (!textMatch) return null;
    
    try {
      const textContent = decodeURIComponent(textMatch[1]);
      const embeddedUrl = extractUrlFromText(textContent);
      
      if (embeddedUrl) {
        return {
          type: 'whatsapp',
          messageText: textContent,
          embeddedUrl: embeddedUrl,
          hasUtm: hasUtmParameters(embeddedUrl)
        };
      }
    } catch (e) {
      console.warn('Error processing WhatsApp link:', e);
    }
    
    return null;
  }
  
  /**
   * Process Telegram links
   *
   * @param {string} url The Telegram URL
   * @return {Object|null} Parsed data or null
   */
  function processTelegramLink(url) {
    const urlMatch = url.match(/(https?:\/\/[^\s&]+)/i);
    if (urlMatch) {
      return {
        type: 'telegram',
        embeddedUrl: urlMatch[0],
        hasUtm: hasUtmParameters(urlMatch[0])
      };
    }

    return null;
  }

  /**
   * Process VK links
   *
   * @param {string} url The VK URL
   * @return {Object|null} Parsed data or null
   */
  function processVkLink(url) {
    const urlMatch = url.match(/(https?:\/\/[^\s&]+)/i);
    if (urlMatch) {
      return {
        type: 'vk',
        embeddedUrl: urlMatch[0],
        hasUtm: hasUtmParameters(urlMatch[0])
      };
    }

    return null;
  }
  
  /**
   * Process Viber links
   * 
   * @param {string} url The Viber URL
   * @return {Object|null} Parsed data or null
   */
  function processViberLink(url) {
    const textMatch = url.match(/[?&]text=([^&]+)/i);
    if (!textMatch) return null;
    
    try {
      const textContent = decodeURIComponent(textMatch[1]);
      const embeddedUrl = extractUrlFromText(textContent);
      
      if (embeddedUrl) {
        return {
          type: 'viber',
          messageText: textContent,
          embeddedUrl: embeddedUrl,
          hasUtm: hasUtmParameters(embeddedUrl)
        };
      }
    } catch (e) {
      console.warn('Error processing Viber link:', e);
    }
    
    return null;
  }
  
  /**
   * Extract an embedded URL from a `url=`, `link=`, or `u=` query parameter.
   * Used by the generic messenger fallback, and by parseMessagingUrlForRewrite()
   * to correct Telegram/VK share-widget extraction on the rewrite-only path
   * (see that function's doc comment for why this must NOT feed back into
   * processTelegramLink/processVkLink themselves).
   *
   * @param {string} url The URL to search
   * @return {string|null} The decoded embedded URL, or null if none found
   */
  export function extractUrlParam(url) {
    const urlParamMatches = url.match(/(?:[?&])(?:url|link|u)=([^&]+)/gi);
    if (!urlParamMatches?.length) return null;

    for (const match of urlParamMatches) {
      const paramValue = match.split('=')[1];
      if (!paramValue) continue;

      try {
        const embedded = decodeURIComponent(paramValue);
        if (/^https?:\/\//.test(embedded)) {
          return embedded;
        }
      } catch (e) {
        // Continue to the next match if this one fails
        continue;
      }
    }

    return null;
  }

  /**
   * Process generic messaging links with URL parameters
   *
   * @param {string} url The URL to process
   * @return {Object|null} Parsed data or null
   */
  function processGenericMessagingLink(url) {
    const embedded = extractUrlParam(url);
    if (!embedded) return null;

    return {
      type: 'generic',
      embeddedUrl: embedded,
      hasUtm: hasUtmParameters(embedded)
    };
  }
  
  /**
   * Extract and parse embedded URLs from messaging links
   * 
   * @param {string} url The messaging URL to examine
   * @return {Object|null} Object with extracted info or null
   */
  export function parseMessagingUrl(url) {
    if (!url || typeof url !== 'string') return null;
    
    try {
      const lowerUrl = url.toLowerCase();
      
      // Handle specific messenger types. WhatsApp's web share links
      // (wa.me/, api.whatsapp.com/send, chat.whatsapp.com) carry the same
      // `?text=` shape as the whatsapp: protocol scheme, so route them the
      // same way — isMessagingLink() already recognizes them via
      // MESSENGER_PATTERNS, but until this fix parseMessagingUrl() fell
      // through to the generic (url=/link=/u=) parser and returned null for
      // them, which silently broke messenger-rewrite for the most common
      // real-world WhatsApp share form.
      if (
        lowerUrl.startsWith('whatsapp:') ||
        lowerUrl.includes('wa.me/') ||
        lowerUrl.includes('api.whatsapp.com/send') ||
        lowerUrl.includes('chat.whatsapp.com')
      ) {
        return processWhatsAppLink(url);
      } else if (lowerUrl.startsWith('tg:') || lowerUrl.includes('t.me/')) {
        return processTelegramLink(url);
      } else if (lowerUrl.startsWith('vk:') || lowerUrl.includes('vk.com/')) {
        return processVkLink(url);
      } else if (lowerUrl.startsWith('viber:')) {
        return processViberLink(url);
      }
      
      // Try generic parsing as fallback
      return processGenericMessagingLink(url);
      
    } catch (e) {
      console.warn('Error parsing messaging URL:', e);
      return null;
    }
  }

  /**
   * Like parseMessagingUrl(), but for Telegram/VK links prefers the
   * `url=`/`link=`/`u=` param over the bare https?:// scan (their share
   * widgets — e.g. https://t.me/share/url?url=<enc> — use that param shape;
   * the bare scan matches the t.me/vk.com prefix itself instead of the
   * param's value whenever the href starts with https://).
   *
   * This correction is DELIBERATELY NOT applied inside parseMessagingUrl()
   * itself: parseMessagingUrl() is also called, for EVERY link (not just
   * messaging ones), by hasEmbeddedUtmParameters() and appendUtmParams() —
   * the general-purpose exclusion path that runs whether or not the opt-in
   * messenger-rewrite feature is enabled. Changing processTelegramLink /
   * processVkLink's output would flip shouldExcludeLink()'s decision for
   * non-messenger-pattern links like `vk.com/share.php?url=...` (not itself
   * a recognized MESSENGER_PATTERN) even with messengerRewrite=false. Use
   * this function ONLY from the messenger-rewrite path (rewriteMessagingUrl /
   * tryRewriteMessengerLink), never from the exclusion path.
   *
   * @param {string} url The messaging URL to examine
   * @return {Object|null} Same shape as parseMessagingUrl()
   */
  export function parseMessagingUrlForRewrite(url) {
    const parsed = parseMessagingUrl(url);
    if (!parsed || (parsed.type !== 'telegram' && parsed.type !== 'vk')) {
      return parsed;
    }

    const paramUrl = extractUrlParam(url);
    if (!paramUrl || paramUrl === parsed.embeddedUrl) {
      return parsed;
    }

    return {
      ...parsed,
      embeddedUrl: paramUrl,
      hasUtm: hasUtmParameters(paramUrl)
    };
  }

  /**
   * Check for UTM parameters in WhatsApp text content
   * 
   * @param {string} url The WhatsApp URL
   * @return {boolean} True if contains UTM parameters
   */
  function checkWhatsAppForUtm(url) {
    try {
      // Check URL directly first — case-insensitive, same reasoning as
      // hasUtmParameters() above (F1, T3): aligns this inline check with the
      // shared helper instead of drifting case-sensitive.
      if (UTM_PARAMETERS.some(param => url.toLowerCase().includes(param))) {
        return true;
      }

      // Extract and check text parameter
      const textMatch = url.match(/[?&]text=([^&]+)/i);
      if (!textMatch || !textMatch[1]) return false;

      let decodedText;
      try {
        decodedText = decodeURIComponent(textMatch[1]);
      } catch (e) {
        // If decoding fails, use original
        console.warn('UTM Linker: MessagingParser decodeURIComponent failed, using raw:', e.message);
        decodedText = textMatch[1];
      }

      // Check if text contains UTM parameters directly (case-insensitive).
      if (UTM_PARAMETERS.some(param => decodedText.toLowerCase().includes(param))) {
        return true;
      }
      
      // Check for embedded URLs with UTM parameters
      const urlMatches = decodedText.match(/https?:\/\/[^\s]+/gi) || [];
      return urlMatches.some(match => hasUtmParameters(match));
      
    } catch (e) {
      console.warn('Error checking WhatsApp URL for UTMs:', e);
      return false;
    }
  }
  
  /**
   * Check if an embedded URL in a messaging link already has UTM parameters
   * 
   * @param {string} url The messaging URL to check
   * @return {boolean} True if the embedded URL has UTM parameters
   */
  export function hasEmbeddedUtmParameters(url) {
    if (!url || typeof url !== 'string') return false;
    
    // Special handling for WhatsApp links
    if (url.toLowerCase().startsWith('whatsapp:')) {
      return checkWhatsAppForUtm(url);
    }
    
    // General check for other messengers
    try {
      const parsedUrl = parseMessagingUrl(url);
      return !!parsedUrl?.hasUtm;
    } catch (e) {
      console.warn('UTM Linker: MessagingParser hasExistingUtm check failed:', e.message);
      return false;
    }
  }
  
  /**
   * Extract the real URL from a messaging link if possible
   * 
   * @param {string} url The messaging URL
   * @return {string|null} The embedded URL or null if not found
   */
  export function extractEmbeddedUrl(url) {
    const parsedUrl = parseMessagingUrl(url);
    return parsedUrl?.embeddedUrl || null;
  }